import React, { useState } from "react";
import Modal from "./ui/Modal";
import { ListSkeleton } from "./ui/Skeleton";
import { EmptyState, ErrorState } from "./ui/StateBlock";
import ChangeBadge from "./ChangeBadge";
import { formatNumber } from "./charts/chartTheme";
import { useApiData } from "../hooks/useApiData";
import { percentChange } from "../hooks/usePeriodStats";

// One fixed row: label on the left, value and change on the right, so Prosek and Medijana always line up.
function Row({ label, value, previous }) {
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <span className="text-sm text-slate-600 dark:text-slate-300">{label}</span>
      <span className="flex items-center gap-3">
        <span className="w-32 text-right text-lg font-semibold tabular-nums text-slate-900 dark:text-slate-50">
          {formatNumber(value, 2)} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">RSD</span>
        </span>
        <span className="flex w-20 justify-end">
          <ChangeBadge changePct={percentChange(value, previous)} />
        </span>
      </span>
    </div>
  );
}

/**
 * Average and median receipt of the selected period, each compared with the period chosen in the Poređenje toggle
 * (compareLabel + compareRange say which one). The median is the middle receipt when the receipts are sorted by amount.
 */
export default function MedianReceiptModal({ open, onClose, query, compareQuery, compareLabel, compareRange }) {
  return (
    <Modal open={open} onClose={onClose} title="Prosečan račun i medijana">
      <MedianBody query={query} compareQuery={compareQuery} compareLabel={compareLabel} compareRange={compareRange} />
    </Modal>
  );
}

function MedianBody({ query, compareQuery, compareLabel, compareRange }) {
  const [retryTick, setRetryTick] = useState(0);
  const cur = useApiData(`/median_receipt.php?${query}`, retryTick);
  const prev = useApiData(compareQuery ? `/median_receipt.php?${compareQuery}` : null, retryTick);
  const failed = Boolean(cur.error) || (cur.data && cur.data.success === false);

  if (failed) return <ErrorState message={cur.data?.error} onRetry={() => setRetryTick((t) => t + 1)} />;
  if (cur.loading || !cur.data || (compareQuery && prev.loading && !prev.data)) return <ListSkeleton rows={2} />;
  if (cur.data.ukupno === 0) return <EmptyState title="Nema računa u izabranom periodu" hint="Probaj drugi period ili magacin." />;

  const p = prev.data?.success ? prev.data : null;
  return (
    <div className="flex flex-col gap-2">
      <div className="divide-y divide-slate-200 dark:divide-slate-800">
        <Row label="Prosek" value={cur.data.prosek} previous={p?.prosek} />
        <Row label="Medijana" value={cur.data.medijana} previous={p?.medijana} />
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        Promena u odnosu na {compareLabel}
        {compareRange ? ` (${compareRange})` : ""}
      </p>
    </div>
  );
}
